import { CTAButton } from "./CTAButton";
import NavDrawer from "./NavDrawer";
import { cn } from "../lib/utils";
import { Link, useRouter } from "@tanstack/react-router";
import { ChevronDown, Menu, Phone } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const desktopNavItems = [
  { label: "Home", href: "/", isAnchor: false },
  {
    label: "Programs",
    href: "/programs",
    isAnchor: false,
    submenu: [
      { label: "Adult BJJ", href: "/programs/jiu-jitsu", isRoute: true },
      { label: "Kids BJJ", href: "/programs/kids-jiu-jitsu", isRoute: true },
      { label: "Teens BJJ", href: "/programs/teens-jiu-jitsu", isRoute: true },
      { label: "MMA", href: "/programs/mma", isRoute: true },
      { label: "Muay Thai", href: "/programs/muay-thai", isRoute: true },
      { label: "Wrestling", href: "/programs/wrestling", isRoute: true },
    ],
  },
  { label: "Schedule", href: "/schedule", isAnchor: false },
  { label: "Reviews", href: "/reviews", isAnchor: false },
  { label: "About", href: "/about-us", isAnchor: false },
  { label: "Contact", href: "/contact-us", isAnchor: false },
];

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
}

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [programsHover, setProgramsHover] = useState(false);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const router = useRouter();

  // Scroll to top on every route change using router.subscribe
  useEffect(() => {
    const unsub = router.subscribe("onLoad", () => {
      window.scrollTo({ top: 0, behavior: "instant" });
    });
    return () => unsub();
  }, [router]);

  // Auto-close mobile drawer when resizing to desktop width
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 768) {
        setDrawerOpen(false);
      }
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function openProgramsDropdown() {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setProgramsHover(true);
  }

  function closeProgramsDropdown() {
    hoverTimeout.current = setTimeout(() => setProgramsHover(false), 120);
  }

  function handleLogoClick() {
    if (router.state.location.pathname === "/") {
      scrollTo("hero");
    } else {
      router.navigate({ to: "/" });
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Top utility bar */}
      <div className="bg-primary text-primary-foreground text-xs py-2 px-4 text-center font-body hidden sm:block">
        <span className="inline-flex items-center gap-2">
          <Phone className="w-3 h-3" />
          Call us: (541) 600-3371 &nbsp;|&nbsp; 1485 Mohawk Blvd, Springfield,
          OR 97477
        </span>
      </div>

      {/* Sticky header */}
      <header
        className="sticky top-0 z-30 bg-card border-b border-border shadow-elevated"
        data-ocid="nav"
      >
        <div className="container mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          {/* Logo */}
          <button
            type="button"
            onClick={handleLogoClick}
            className="flex items-center gap-3 group"
            aria-label="Superhero Jiu Jitsu Academy — Home"
          >
            <div className="w-12 h-15 rounded-lg flex items-center justify-center flex-shrink-0">
              <img src="/assets/images/superhero-jiu-jitsu-academy-logo-01.png" />
            </div>
          </button>

          {/* Desktop nav */}
          <nav
            className="hidden md:flex items-center gap-1"
            aria-label="Main navigation"
          >
            {desktopNavItems.map((item) => {
              if (item.submenu) {
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={openProgramsDropdown}
                    onMouseLeave={closeProgramsDropdown}
                  >
                    <Link
                      to="/programs"
                      onClick={() => setProgramsHover(false)}
                      className="inline-flex items-center gap-1 px-4 py-2 text-sm font-body font-medium text-muted-foreground hover:text-foreground transition-colors-fast rounded-md hover:bg-muted/60"
                      activeProps={{ className: "text-foreground bg-muted/60" }}
                      data-ocid="nav-programs"
                    >
                      {item.label}
                      <ChevronDown
                        className={cn(
                          "w-3.5 h-3.5 transition-transform duration-200",
                          programsHover && "rotate-180",
                        )}
                      />
                    </Link>

                    {/* Dropdown */}
                    <div
                      className={cn(
                        "absolute top-full left-0 mt-1 w-44 bg-card border border-border rounded-xl shadow-elevated overflow-hidden",
                        "transition-all duration-200 origin-top",
                        programsHover
                          ? "opacity-100 scale-y-100 pointer-events-auto"
                          : "opacity-0 scale-y-95 pointer-events-none",
                      )}
                    >
                      <div className="h-0.5 bg-gradient-accent" />
                      <ul className="py-1.5">
                        {item.submenu.map((sub) => (
                          <li key={sub.label}>
                            {sub.isRoute ? (
                              <Link
                                to={sub.href}
                                onClick={() => setProgramsHover(false)}
                                className="block w-full text-left px-4 py-2 text-sm font-body text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors-fast cursor-pointer"
                              >
                                {sub.label}
                              </Link>
                            ) : (
                              <button
                                type="button"
                                onClick={() => scrollTo("programs")}
                                className="w-full text-left px-4 py-2 text-sm font-body text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors-fast cursor-pointer"
                              >
                                {sub.label}
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              }

              if (!item.isAnchor) {
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="px-4 py-2 text-sm font-body font-medium text-muted-foreground hover:text-foreground transition-colors-fast rounded-md hover:bg-muted/60"
                    activeProps={{ className: "text-foreground bg-muted/60" }}
                    data-ocid={`nav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </Link>
                );
              }

              return (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollTo(item.href.replace("#", ""));
                  }}
                  className="px-4 py-2 text-sm font-body font-medium text-muted-foreground hover:text-foreground transition-colors-fast rounded-md hover:bg-muted/60"
                >
                  {item.label}
                </a>
              );
            })}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <CTAButton
              variant="hero"
              size="sm"
              onClick={() => router.navigate({ to: "/contact-us" })}
              data-ocid="nav-cta"
            >
              Free Class
            </CTAButton>
          </div>

          {/* Mobile hamburger — opens right-to-left drawer */}
          <button
            type="button"
            className="md:hidden p-2 rounded-md hover:bg-muted transition-colors-fast"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={drawerOpen}
            data-ocid="nav-hamburger"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Right-to-left slide drawer for mobile */}
      <NavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      {/* Main content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 sm:px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                  <img src="/assets/images/superhero-jiu-jitsu-academy-logo-02.png" />
                </div>                
              </div>
              <p className="text-sm opacity-70 leading-relaxed">
                Springfield's premier martial arts academy. Building confidence,
                discipline, and real-world skills since 2014.
              </p>
            </div>

            {/* Programs */}
            <div>
              <h4 className="font-display font-semibold text-sm tracking-widest uppercase text-gold mb-4">
                Programs
              </h4>
              <ul className="space-y-2 text-sm opacity-80">
                <li>
                  <Link
                    to="/programs/jiu-jitsu"
                    className="hover:text-gold transition-colors-fast cursor-pointer"
                  >
                    Jiu Jitsu
                  </Link>
                </li>
                <li>
                  <Link
                    to="/programs/kids-jiu-jitsu"
                    className="hover:text-gold transition-colors-fast cursor-pointer"
                  >
                    Kids Jiu Jitsu
                  </Link>
                </li>
                <li>
                  <Link
                    to="/programs/teens-jiu-jitsu"
                    className="hover:text-gold transition-colors-fast cursor-pointer"
                  >
                    Teens Jiu Jitsu
                  </Link>
                </li>
                {["Wrestling"].map((p) => (
                  <li key={p}>
                    <Link
                      to="/programs/wrestling"
                      className="hover:text-gold transition-colors-fast cursor-pointer"
                    >
                      {p}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/programs/muay-thai"
                    className="hover:text-gold transition-colors-fast cursor-pointer"
                  >
                    Muay Thai
                  </Link>
                </li>
                <li>
                  <Link
                    to="/programs/mma"
                    className="hover:text-gold transition-colors-fast cursor-pointer"
                  >
                    MMA
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-display font-semibold text-sm tracking-widest uppercase text-gold mb-4">
                Location
              </h4>
              <address className="not-italic text-sm opacity-80 space-y-1 leading-relaxed">
                <p>1485 Mohawk Blvd</p>
                <p>Springfield, OR 97477</p>
                <p className="pt-2">
                  <a
                    href="tel:+15416003371"
                    className="hover:text-gold transition-colors-fast"
                  >
                    (541) 600-3371
                  </a>
                </p>
                <p>
                  <a
                    href="mailto:shjjoffice@gmail.com"
                    className="hover:text-gold transition-colors-fast"
                  >
                    shjjoffice@gmail.com
                  </a>
                </p>
              </address>
            </div>
          </div>

          <div className="border-t border-primary-foreground/20 pt-6 flex flex-col items-center gap-3 text-xs opacity-60">
            <div className="flex items-center gap-4">
              <Link
                to="/privacy-policy"
                className="hover:opacity-100 hover:text-gold transition-colors-fast cursor-pointer"
              >
                Privacy Policy
              </Link>
              <span className="opacity-40">|</span>
              <Link
                to="/terms-and-conditions"
                className="hover:opacity-100 hover:text-gold transition-colors-fast cursor-pointer"
              >
                Terms of Use
              </Link>
            </div>
            <p className="text-center">
              © {new Date().getFullYear()} Superhero Jiu Jitsu Academy. All
              Rights Reserved. Developed by{" "}
              <a
                href="https://redbeltsoftware.com"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-gold transition-colors cursor-pointer"
              >
                Red Belt Software
              </a>{" "}
              websites.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
